import { FC, useCallback, useState } from "react"
import { AppDetailBase } from "../../../../AppDetailBase/AppDetailBase"
import { Button, ButtonGroup } from "dash-styled"
import { useAppInstance } from "../../../../../../../../../Hooks/useAppInstance"
import {
    getValue,
    hasResult
} from "../../../../../../../../../Models/Client/FetchState"
import { useDispatch } from "react-redux"
import { MainThunkDispatch } from "../../../../../../../../../Reducers"
import { updateInstance } from "../../../../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { TabButton } from "../../../../../../../../Common/Button/TabButton"
import { LLMParams } from "./LLMParams"
import { PromptInput } from "./PromptInput"

interface AppCustomizeLLMParametersProps {
    orgUsername: string
    appID: string
}

const ComponentsSchema = [
    {
        key: "name",
        title: "Name",
        rows: 1
    },
    {
        key: "role",
        title: "Role",
        rows: 4
    },
    {
        key: "advice",
        title: "Advice prompt",
        rows: 4,
        isArray: true
    },
    {
        key: "goals",
        title: "Goal",
        rows: 4,
        isArray: true
    },
    {
        key: "constraints",
        title: "Constraints",
        rows: 4,
        isArray: true
    },
    {
        key: "prompt_prefix",
        title: "Prompt prefix",
        rows: 4
    },
    {
        key: "prompt_suffix",
        title: "Prompt suffix",
        rows: 4
    }
]

const PromptSchema = [
    {
        key: "tableIdentification",
        title: "Table Identification LLM",
        components: ComponentsSchema
    },
    {
        key: "valueExtraction",
        title: "Value Extraction LLM",
        components: ComponentsSchema
    }
]

const tabs = [
    {
        key: "llmParams",
        title: "LLM Params"
    },
    ...PromptSchema
]

export const AppCustomizeLLMParameters: FC<AppCustomizeLLMParametersProps> = ({
    orgUsername,
    appID
}) => {
    const instanceFetch = useAppInstance(appID)
    const instance = getValue(instanceFetch)

    const [prompts, setPrompts] = useState(instance?.appParams?.params.prompt)
    const [llmParams, setLlmParams] = useState(
        instance?.appParams?.params.llmParams ?? {
            max_fields_in_prompt: 0,
            foundation_model: "gpt-35-turbo-0301-eu",
            document_representation: "line",
            table_matching_method: "ocr_tables"
        }
    )

    const [tab, setTab] = useState<string>("llmParams")

    const dispatch = useDispatch<MainThunkDispatch>()
    const handleSubmit = useCallback(() => {
        const patch = {
            appParams: {
                params: {
                    ...instance?.appParams?.params,
                    prompt: prompts,
                    llmParams: llmParams
                }
            }
        }
        dispatch(
            updateInstance(
                appID,
                patch,
                "LLM parameters changes saved",
                "LLM parameters change failed"
            )
        ).catch(() => {})
    }, [instance, prompts, llmParams, appID, dispatch])

    const tabRenderers: { [key: string]: () => JSX.Element } = {
        llmParams: () => (
            <LLMParams llmParams={llmParams} onChange={setLlmParams} />
        ),
        ...PromptSchema.reduce(
            (acc, group) => ({
                ...acc,
                [group.key]: () => (
                    <>
                        {group.components.map(component => (
                            <PromptInput
                                key={`${group.key}-${component.key}`}
                                title={component.title}
                                componentKey={component.key}
                                groupKey={group.key}
                                prompts={prompts}
                                rows={component.rows}
                                isArray={component.isArray}
                                onChange={setPrompts}
                            />
                        ))}
                    </>
                )
            }),
            {}
        )
    }

    if (!hasResult(instanceFetch)) {
        return null
    }

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title="Project LLM Settings"
            breadcrumbs={[]}
            fixedContentWidth={true}
        >
            <ButtonGroup equalWidth={true}>
                {tabs.map(group => (
                    <TabButton
                        key={group.key}
                        onClick={() => setTab(group.key)}
                        active={tab === group.key}
                    >
                        {group.title}
                    </TabButton>
                ))}
            </ButtonGroup>

            {tabRenderers[tab]()}
            <div className="w-full flex flex-col items-center p-6">
                <Button
                    color="darkviolet"
                    variant="contained"
                    onClick={handleSubmit}
                    testId="apply-parameters"
                    className="w-auto px-12"
                >
                    Save changes
                </Button>
            </div>
        </AppDetailBase>
    )
}
